/**
 * NDP cross-sheet validation — builds a provider-by-column validation grid
 * comparing all earnings/deposit sheets against the NDP Report (reference).
 */

const normalizeProviderName = (name) => {
  if (!name) return '';
  return name.replace(/^Dr\.\s+/i, '').trim().toLowerCase();
};

const formatCurrency = (value) => {
  if (typeof value !== 'number' || isNaN(value)) return '0.00';
  return value.toFixed(2);
};

const colLetter = (index) => {
  return String.fromCharCode(65 + index);
};

/**
 * @param {Object} input
 * @param {Array} input.ndpRows - NDP Report rows (all levels; filter to level 1)
 * @param {Array} input.sagePageRows - SAGE Page rows
 * @param {Array} input.sageExportRows - SAGE EXPORT rows
 * @param {Array} input.funding1Rows - FUNDING 1ST rows
 * @param {Array} input.funding2Rows - FUNDING 2ND rows
 * @param {Array} input.ukgRows - UKG Page/EXPORT rows
 * @param {Array} input.deposit1Rows - DEPOSIT 1ST rows
 * @param {Array} input.deposit2Rows - DEPOSIT 2ND rows
 * @param {Array} input.adp1Rows - ADP 1 rows
 * @param {Array} input.adp2Rows - ADP 2 rows
 * @param {Array} input.financialNotesRows - Financial Notes rows
 * @returns {Object} { rows, columns, excelRows, cellHighlights: {green, red}, sageDoubleEntryCheck }
 */
export const buildNdpValidationData = ({
  ndpRows = [],
  sagePageRows = [],
  sageExportRows = [],
  funding1Rows = [],
  funding2Rows = [],
  ukgRows = [],
  deposit1Rows = [],
  deposit2Rows = [],
  adp1Rows = [],
  adp2Rows = [],
  financialNotesRows = []
}) => {
  const TOLERANCE = 0.01;

  // Step 1: Extract provider-level references from NDP Report (level 1 rows only)
  const providers = (ndpRows || [])
    .filter(r => r && r.level === 1)
    .map(r => ({
      provider: r.Provider,
      abbr: r.ABBR,
      nk: normalizeProviderName(r.Provider),
      refFirstDeposit: Number(r['First Deposit'] || 0),
      refSecondDeposit: Number(r['Second Deposit'] || 0),
      refTotalPay: Number(r['Total Pay'] || 0),
      refFinancialNote: Number(r['Financial Note'] || 0)
    }));

  // Step 2: Build summed totals per provider from each sheet
  const buildSheetsMap = (rows, providerKey, amountKey, isCurrency = true) => {
    const map = new Map();
    (rows || []).forEach(row => {
      if (!row || !row[providerKey]) return;
      const pk = normalizeProviderName(row[providerKey]);
      const amt = isCurrency ? Number(row[amountKey] || 0) : (row[amountKey] || 0);
      map.set(pk, (map.get(pk) || 0) + amt);
    });
    return map;
  };

  const sagePage = buildSheetsMap(sagePageRows, 'PROVIDER', 'Debit');
  const sageExport = buildSheetsMap(sageExportRows, 'PROVIDER', 'DEBIT');
  const funding1 = buildSheetsMap(funding1Rows, 'PROVIDER', 'Pay Amt');
  const funding2 = buildSheetsMap(funding2Rows, 'PROVIDER', 'Pay Amt');
  const ukgRows1 = buildSheetsMap(ukgRows, 'PROVIDER', '1ST DEPOSIT');
  const ukgRows2 = buildSheetsMap(ukgRows, 'PROVIDER', '2ND DEPOSIT');
  const ukgRowsTotal = buildSheetsMap(ukgRows, 'PROVIDER', 'AMOUNT');
  const deposit1 = buildSheetsMap(deposit1Rows, 'Username', 'E/D/T Amount');
  const deposit2 = buildSheetsMap(deposit2Rows, 'Username', 'E/D/T Amount');
  const adp1 = buildSheetsMap(adp1Rows, 'Provider', 'Earnings 3 Amount');
  const adp2 = buildSheetsMap(adp2Rows, 'Provider', 'Earnings 3 Amount');
  const fnotes = buildSheetsMap(financialNotesRows, 'Provider', 'Amount');

  // Step 3: Build validation rows and cell data
  const columns = [
    { key: 'provider', label: 'Provider', colored: false },
    { key: 'abbr', label: 'ABBR', colored: false },
    { key: 'ndf1', label: 'NDP First Deposit', colored: false },
    { key: 'f1f1', label: 'FUNDING 1ST Pay Amt', colored: true },
    { key: 'ukgd1', label: 'UKG 1ST DEPOSIT', colored: true },
    { key: 'dep1', label: 'DEPOSIT 1ST Total', colored: true },
    { key: 'adp1a', label: 'ADP 1 Earnings', colored: true },
    { key: 'ndf2', label: 'NDP Second Deposit', colored: false },
    { key: 'f1f2', label: 'FUNDING 2ND Pay Amt', colored: true },
    { key: 'ukgd2', label: 'UKG 2ND DEPOSIT', colored: true },
    { key: 'dep2', label: 'DEPOSIT 2ND Total', colored: true },
    { key: 'adp2a', label: 'ADP 2 Earnings', colored: true },
    { key: 'ndtp', label: 'NDP Total Pay', colored: false },
    { key: 'sgp', label: 'SAGE Page Debit', colored: true },
    { key: 'sgx', label: 'SAGE EXPORT Debit', colored: true },
    { key: 'ukgt', label: 'UKG Total Amount', colored: true },
    { key: 'ndfn', label: 'NDP Financial Note', colored: false },
    { key: 'ndsfn', label: 'Financial Notes Total', colored: true }
  ];

  const rows = providers.map(p => {
    const cells = {
      provider: { value: p.provider, pass: null },
      abbr: { value: p.abbr, pass: null },
      ndf1: { value: formatCurrency(p.refFirstDeposit), pass: null },
      f1f1: {
        value: formatCurrency(funding1.get(p.nk) || 0),
        pass: Math.abs((funding1.get(p.nk) || 0) - p.refFirstDeposit) < TOLERANCE
      },
      ukgd1: {
        value: formatCurrency(ukgRows1.get(p.nk) || 0),
        pass: Math.abs((ukgRows1.get(p.nk) || 0) - p.refFirstDeposit) < TOLERANCE
      },
      dep1: {
        value: formatCurrency(deposit1.get(p.nk) || 0),
        pass: Math.abs((deposit1.get(p.nk) || 0) - p.refFirstDeposit) < TOLERANCE
      },
      adp1a: {
        value: formatCurrency(adp1.get(p.nk) || 0),
        pass: Math.abs((adp1.get(p.nk) || 0) - p.refFirstDeposit) < TOLERANCE
      },
      ndf2: { value: formatCurrency(p.refSecondDeposit), pass: null },
      f1f2: {
        value: formatCurrency(funding2.get(p.nk) || 0),
        pass: Math.abs((funding2.get(p.nk) || 0) - p.refSecondDeposit) < TOLERANCE
      },
      ukgd2: {
        value: formatCurrency(ukgRows2.get(p.nk) || 0),
        pass: Math.abs((ukgRows2.get(p.nk) || 0) - p.refSecondDeposit) < TOLERANCE
      },
      dep2: {
        value: formatCurrency(deposit2.get(p.nk) || 0),
        pass: Math.abs((deposit2.get(p.nk) || 0) - p.refSecondDeposit) < TOLERANCE
      },
      adp2a: {
        value: formatCurrency(adp2.get(p.nk) || 0),
        pass: Math.abs((adp2.get(p.nk) || 0) - p.refSecondDeposit) < TOLERANCE
      },
      ndtp: { value: formatCurrency(p.refTotalPay), pass: null },
      sgp: {
        value: formatCurrency(sagePage.get(p.nk) || 0),
        pass: Math.abs((sagePage.get(p.nk) || 0) - p.refTotalPay) < TOLERANCE
      },
      sgx: {
        value: formatCurrency(sageExport.get(p.nk) || 0),
        pass: Math.abs((sageExport.get(p.nk) || 0) - p.refTotalPay) < TOLERANCE
      },
      ukgt: {
        value: formatCurrency(ukgRowsTotal.get(p.nk) || 0),
        pass: Math.abs((ukgRowsTotal.get(p.nk) || 0) - p.refTotalPay) < TOLERANCE
      },
      ndfn: { value: formatCurrency(p.refFinancialNote), pass: null },
      ndsfn: {
        value: formatCurrency(fnotes.get(p.nk) || 0),
        pass: Math.abs((fnotes.get(p.nk) || 0) - p.refFinancialNote) < TOLERANCE
      }
    };
    return { provider: p.provider, abbr: p.abbr, cells };
  });

  // Step 4: Build Excel rows (plain objects keyed by column label)
  const excelRows = rows.map(r => {
    const obj = {};
    columns.forEach(col => {
      obj[col.label] = r.cells[col.key].value || '';
    });
    return obj;
  });

  // Step 5: Build cell highlights for Excel coloring (1-indexed, data rows start at 2)
  const green = [];
  const red = [];
  rows.forEach((r, rowIdx) => {
    const excelRowNum = rowIdx + 2;
    columns.forEach((col, colIdx) => {
      if (!col.colored) return;
      const cell = r.cells[col.key];
      const cellAddr = `${colLetter(colIdx)}${excelRowNum}`;
      if (cell.pass === true) {
        green.push(cellAddr);
      } else if (cell.pass === false) {
        red.push(cellAddr);
      }
    });
  });

  // Step 6: SAGE Double-Entry Rule Validation
  // Check if each SAGE entry has a corresponding negating Account 2230 entry
  const sageDoubleEntryCheck = {
    sagePageCheck: validateSageDoubleEntry(sagePageRows, 'PROVIDER', 'Debit'),
    sageExportCheck: validateSageDoubleEntry(sageExportRows, 'PROVIDER', 'DEBIT')
  };

  return {
    rows,
    columns,
    excelRows,
    cellHighlights: { green, red },
    sageDoubleEntryCheck
  };
};

/**
 * Validates SAGE sheets for double-entry pattern:
 * Each record should have a negating Account 2230 record, totaling to 0
 */
const validateSageDoubleEntry = (rows, providerKey, amountKey) => {
  const TOLERANCE = 0.01;
  const result = {
    isValid: true,
    practitioners: {},
    summary: ''
  };

  if (!rows || rows.length === 0) {
    result.summary = 'No SAGE rows to validate';
    return result;
  }

  // Determine account field name (SAGE Page uses "ACCT No.", SAGE EXPORT uses 'ACCT_NO')
  let accountField = 'ACCT_NO';
  if (rows.length > 0 && rows[0] && rows[0]['ACCT No.'] !== undefined) {
    accountField = 'ACCT No.';
  }

  // Group by practice to check double-entry accounting per practice
  const byPractice = new Map();
  rows.forEach(row => {
    if (!row) return;
    const practice = row.PRACTICE || 'Unknown';
    if (!byPractice.has(practice)) {
      byPractice.set(practice, []);
    }
    byPractice.get(practice).push(row);
  });

  // Check each practice for double-entry rule
  let totalValid = 0;
  byPractice.forEach((entries, practice) => {
    const hasAccount2230 = entries.some(e => (e[accountField] || '').toString() === '2230');
    const totalAmount = entries.reduce((sum, e) => sum + Number(e[amountKey] || 0), 0);

    const practiceValid = hasAccount2230 && Math.abs(totalAmount) < TOLERANCE;

    result.practitioners[practice] = {
      hasAccount2230,
      totalAmount: totalAmount.toFixed(2),
      isValid: practiceValid,
      entryCount: entries.length
    };

    if (practiceValid) totalValid++;
  });

  result.isValid = totalValid === byPractice.size;
  const totalPractices = byPractice.size;
  result.summary = `${totalValid}/${totalPractices} practices follow double-entry rule (total = 0 with Account 2230)`;

  return result;
};
